import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ApiError, del, post } from '../lib/api.ts'
import { useList } from '../lib/useList.ts'
import { formatDateTime } from '../lib/format.ts'
import { useAuth } from '../lib/auth.tsx'
import { PageHeader } from '../components/AppShell.tsx'
import { ConfirmDialog } from '../components/ConfirmDialog.tsx'
import { useToast } from '../components/Toast.tsx'
import { Button, Empty, Input, SkeletonRows } from '../components/ui.tsx'
import { IconBilling, IconEdit, IconEye, IconPlus, IconSearch, IconTrash } from '../components/icons.tsx'

type Rx = {
  id: string; number: string; prescribedAt: string; lineCount: number
  status: 'draft' | 'sent'
  billState: 'none' | 'with_counter' | 'issued' | 'paid'
  version: number
  patient: { id: string; mrn: string; name: string }
  doctor: { id: string; name: string }
}

export function PrescriptionsPage() {
  const list = useList<Rx>('/api/prescriptions')
  const navigate = useNavigate()
  const toast = useToast()
  const { me, can } = useAuth()
  const [removing, setRemoving] = useState<Rx | null>(null)
  const [sending, setSending] = useState<string | null>(null)

  // Where a prescription has got to, in one word. Paid is the one that
  // matters: past that point it cannot be changed, only replaced.
  const state = (rx: Rx) =>
    rx.billState === 'paid' ? { label: 'Paid', cls: 'final' }
    : rx.billState === 'issued' ? { label: 'Billed', cls: 'doctor' }
    : rx.status === 'sent' ? { label: 'With counter', cls: 'reception' }
    : { label: 'Draft', cls: 'draft' }

  async function sendToBilling(rx: Rx) {
    setSending(rx.id)
    try {
      await post(`/api/prescriptions/${rx.id}/send`, { version: rx.version })
      list.refresh()
      toast.ok('Sent to billing', 'The counter can now raise the bill.')
    } catch (e) {
      toast.fail('Could not send', (e as ApiError).message)
    } finally { setSending(null) }
  }
  const isOwner = me?.staff.role === 'owner'

  return (
    <>
      <PageHeader title="Prescriptions">
        {can('prescription:write') ? (
          <Button variant="primary" onClick={() => navigate('/prescriptions/new')}>
            <IconPlus /> New prescription
          </Button>
        ) : null}
      </PageHeader>

      <div className="content">
        <div className="page-max stack" style={{ gap: 'var(--s-5)' }}>
          <div className="toolbar">
            <div className="search">
              <IconSearch className="ic" />
              <Input placeholder="Search by patient, MRN, number or doctor"
                value={list.q} onChange={e => list.setQ(e.target.value)}
                aria-label="Search prescriptions" />
            </div>
            {!isOwner ? (
              <span className="dim" style={{ fontSize: 'var(--text-sm)' }}>
                Showing prescriptions you wrote
              </span>
            ) : null}
          </div>

          <div className="card">
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Number</th><th>Patient</th><th>Doctor</th>
                    <th>Items</th><th>Status</th><th>Written</th>
                    <th><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {list.loading ? <SkeletonRows rows={5} cols={7} /> : null}
                  {!list.loading && list.items.map(rx => (
                    <tr key={rx.id}>
                      <td className="mono">{rx.number}</td>
                      <td>
                        <div style={{ fontWeight: 550 }}>{rx.patient.name}</div>
                        <div className="dim mono" style={{ fontSize: 'var(--text-xs)' }}>{rx.patient.mrn}</div>
                      </td>
                      <td className="muted">{rx.doctor.name}</td>
                      <td>{rx.lineCount}</td>
                      <td>
                        <span className={`badge ${state(rx).cls}`}>{state(rx).label}</span>
                      </td>
                      <td className="dim">{formatDateTime(rx.prescribedAt)}</td>
                      <td className="actions">
                        <span className="row-actions">
                          {can('prescription:write') && rx.status !== 'sent' && rx.billState === 'none' ? (
                            <Button size="sm" variant="primary" loading={sending === rx.id}
                              style={{ marginRight: 6 }}
                              onClick={() => void sendToBilling(rx)}>
                              <IconBilling size={14} /> Send to billing
                            </Button>
                          ) : null}
                          <button className="icon-btn" title="View" aria-label={`View ${rx.number}`}
                            onClick={() => navigate(`/prescriptions/${rx.id}`)}><IconEye /></button>
                          <button className="icon-btn" title={rx.billState === 'paid' || rx.billState === 'issued'
                              ? 'Closed — a bill has been issued' : 'Edit'}
                            aria-label={`Edit ${rx.number}`}
                            disabled={!can('prescription:write')
                              || (!isOwner && (rx.billState === 'paid' || rx.billState === 'issued'))}
                            onClick={() => navigate(`/prescriptions/${rx.id}/edit`)}><IconEdit /></button>
                          <button className="icon-btn danger" title="Delete" aria-label={`Delete ${rx.number}`}
                            disabled={!can('prescription:write')
                              || (!isOwner && (rx.billState === 'paid' || rx.billState === 'issued'))}
                            onClick={() => setRemoving(rx)}><IconTrash /></button>
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {!list.loading && list.items.length === 0 ? (
              <Empty title={list.q ? 'No matches' : 'No prescriptions yet'}>
                {list.q ? 'Try a different name or number.' : 'Write the first one.'}
              </Empty>
            ) : null}

            {list.error ? (
              <div className="empty">
                <p style={{ color: 'var(--danger)' }}>{list.error}</p>
                <Button onClick={list.refresh} style={{ marginTop: 'var(--s-3)' }}>Try again</Button>
              </div>
            ) : null}
          </div>

          {list.hasMore ? (
            <div className="row" style={{ justifyContent: 'center' }}>
              <Button onClick={list.loadMore} loading={list.loadingMore}>Load more</Button>
            </div>
          ) : null}
        </div>
      </div>

      {removing ? (
        <ConfirmDialog
          title={`Delete ${removing.number}?`}
          body="The prescription is kept, not erased — it stops appearing in this list, and the audit trail still records it."
          confirmLabel="Delete" danger
          onClose={() => setRemoving(null)}
          onConfirm={async () => {
            try {
              await del(`/api/prescriptions/${removing.id}`)
              list.refresh()
              toast.ok('Prescription deleted')
            } catch (e) { toast.fail('Could not delete', (e as ApiError).message) }
          }}
        />
      ) : null}
    </>
  )
}
